import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { env } from 'src/config/env';
import * as ApiSettingService from 'src/db/serviceApiSetting';

// Types
export interface HttpClientConfig {
  baseUrl?: string;
  timeout?: number;
  cacheTtl?: number; // milliseconds, default 5 minutes
}

export interface ApiResponse<T = any> {
  success: boolean;
  data: T | null;
  error: string | null;
  status: number;
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// Default configuration
const DEFAULT_CONFIG: HttpClientConfig = {
  baseUrl: env.SERVER_URL,
  timeout: 30000, // 30 seconds
  cacheTtl: 5 * 60 * 1000, // 5 minutes
};

class HttpClient {
  private static instance: HttpClient;
  private axiosInstance: AxiosInstance;
  private cache: Map<string, CacheEntry<any>> = new Map();
  private config: HttpClientConfig;

  private constructor(config: HttpClientConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.axiosInstance = axios.create({
      baseURL: this.config.baseUrl,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static getInstance(config?: HttpClientConfig): HttpClient {
    if (!this.instance) {
      this.instance = new HttpClient(config);
    }
    return this.instance;
  }

  // Get authentication token
  private async getAuthToken(): Promise<string | null> {
    try {
      const accountAccess = await ApiSettingService.getAccess();
      if (!accountAccess) {
        console.error('[HttpClient] Failed to get access token');
        return null;
      }
      return accountAccess.accessToken;
    } catch (error) {
      console.error('[HttpClient] Error retrieving access token:', error);
      return null;
    }
  }

  // Cache management
  private generateCacheKey(method: string, endpoint: string, params?: any): string {
    // Sort params keys to ensure consistent cache keys regardless of property order
    const paramsStr = params
      ? JSON.stringify(params, Object.keys(params).sort())
      : '';
    return `${method}:${endpoint}:${paramsStr}`;
  }

  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  private setCache<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.cacheTtl!,
    });
  }

  clearCache(pattern?: string): void {
    if (pattern) {
      console.log('[HttpClient] ========== CACHE CLEAR REQUEST ==========');
      console.log('[HttpClient] Pattern to match:', pattern);
      console.log('[HttpClient] Current cache keys:');

      const allKeys = Array.from(this.cache.keys());
      allKeys.forEach((key, index) => {
        const matches = key.includes(pattern);
        console.log(`  ${index + 1}. ${matches ? '✅ MATCH' : '❌ NO MATCH'}: ${key}`);
      });

      let deletedCount = 0;
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          console.log('[HttpClient] 🗑️  DELETING:', key);
          this.cache.delete(key);
          deletedCount++;
        }
      }

      console.log(`[HttpClient] ========== DELETED ${deletedCount} ENTRIES ==========`);
    } else {
      console.log('[HttpClient] Clearing all cache');
      this.cache.clear();
    }
  }

  // Invalidate cache entries related to an endpoint after mutations
  private invalidateCacheForEndpoint(endpoint: string): void {
    // Extract base endpoint (remove query params and IDs)
    const baseEndpoint = endpoint.split('?')[0].replace(/\/\d+$/, '');
    console.log('[HttpClient] Invalidating cache for endpoint:', baseEndpoint);

    let deletedCount = 0;
    for (const key of this.cache.keys()) {
      // Clear any GET requests to the same base endpoint
      if (key.startsWith('GET:') && key.includes(baseEndpoint)) {
        console.log('[HttpClient] 🗑️  Invalidating cache:', key);
        this.cache.delete(key);
        deletedCount++;
      }
    }

    if (deletedCount > 0) {
      console.log(`[HttpClient] Invalidated ${deletedCount} cache entries for ${baseEndpoint}`);
    }
  }

  // Get cache statistics for debugging
  getCacheStats(): { size: number; entries: Array<{ key: string; age: number; ttl: number }> } {
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: Date.now() - entry.timestamp,
      ttl: entry.ttl,
    }));

    return {
      size: this.cache.size,
      entries: entries.sort((a, b) => b.age - a.age), // Sort by age (oldest first)
    };
  }

  // Error handling
  private handleError(error: AxiosError): ApiResponse {
    if (error.response) {
      const status = error.response.status;
      let message = `API Error: ${status}`;

      switch (status) {
        case 401:
          message = 'Authentication failed - please log in again';
          ApiSettingService.clearTokenCache();
          break;
        case 403:
          message = 'Access denied - insufficient permissions';
          break;
        case 404:
          message = 'Resource not found';
          break;
        case 422:
          message = 'Validation error';
          break;
        case 500:
          message = 'Server error - please try again later';
          break;
      }

      // Try to get error message from response
      const responseData = error.response.data as any;
      if (responseData?.error?.message) {
        message = responseData.error.message;
      } else if (responseData?.message) {
        message = responseData.message;
      }

      return { success: false, data: null, error: message, status };
    }

    if (error.code === 'ECONNABORTED') {
      return { success: false, data: null, error: 'Request timeout', status: 408 };
    }

    return { success: false, data: null, error: 'Network error', status: 0 };
  }

  // HTTP Methods
  async get<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    options?: { useCache?: boolean; cacheTtl?: number }
  ): Promise<ApiResponse<T>> {
    const { useCache = true, cacheTtl } = options || {};
    const cacheKey = this.generateCacheKey('GET', endpoint, params);

    console.log('[HttpClient] GET Request Details:', {
      endpoint,
      params,
      cacheKey,
      useCache,
      fullUrl: `${this.axiosInstance.defaults.baseURL}${endpoint}`,
    });

    // Check cache
    if (useCache) {
      const cached = this.getFromCache<T>(cacheKey);
      if (cached) {
        console.log('[HttpClient] ✅ Returning CACHED data for key:', cacheKey)
        return { success: true, data: cached, error: null, status: 200 };
      } else {
        console.log('[HttpClient] ❌ No cache found, making API request');
      }
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, data: null, error: 'No authentication token', status: 401 };
      }

      const response: AxiosResponse<T> = await this.axiosInstance.get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      // Cache successful response
      if (useCache) {
        this.setCache(cacheKey, response.data, cacheTtl);
      }

      // Debug logging for warehouse requests
      if (endpoint.includes('marketplace_warehouses')) {
        console.log('[HttpClient] GET marketplace_warehouses response:', {
          endpoint,
          useCache,
          status: response.status,
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
          meta: (response.data as any)?._meta || 'no _meta',
          dataCount: (response.data as any)?.data?.length || 0,
        })
        console.log('[HttpClient] Full response data:', response.data)
      }

      return { success: true, data: response.data, error: null, status: response.status };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, data: null, error: 'No authentication token', status: 401 };
      }

      const response: AxiosResponse<T> = await this.axiosInstance.post(endpoint, data, {
        ...config,
        headers: { ...config?.headers, Authorization: `Bearer ${token}` },
      });

      // Invalidate related cache entries after successful mutation
      this.invalidateCacheForEndpoint(endpoint);

      return { success: true, data: response.data, error: null, status: response.status };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  async put<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, data: null, error: 'No authentication token', status: 401 };
      }

      const response: AxiosResponse<T> = await this.axiosInstance.put(endpoint, data, {
        ...config,
        headers: { ...config?.headers, Authorization: `Bearer ${token}` },
      });

      // Invalidate related cache entries after successful mutation
      this.invalidateCacheForEndpoint(endpoint);

      return { success: true, data: response.data, error: null, status: response.status };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  async delete<T = any>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, data: null, error: 'No authentication token', status: 401 };
      }

      const response: AxiosResponse<T> = await this.axiosInstance.delete(endpoint, {
        ...config,
        headers: { ...config?.headers, Authorization: `Bearer ${token}` },
      });

      // Invalidate related cache entries after successful mutation
      this.invalidateCacheForEndpoint(endpoint);

      return { success: true, data: response.data, error: null, status: response.status };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  async patch<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, data: null, error: 'No authentication token', status: 401 };
      }

      const response: AxiosResponse<T> = await this.axiosInstance.patch(endpoint, data, {
        ...config,
        headers: { ...config?.headers, Authorization: `Bearer ${token}` },
      });

      // Invalidate related cache entries after successful mutation
      this.invalidateCacheForEndpoint(endpoint);

      return { success: true, data: response.data, error: null, status: response.status };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }
}

export default HttpClient;
